import {FC, useCallback, useState} from "react";
import apiClient from "../lib/api.ts";
import UploadButton from "@memaro/components/UploadButton.tsx";
import Section, {SectionProps} from "../base/Section.tsx";
import AudioPlayer from "./Audio/AudioPlayer.tsx";
import ButtonOverlay from "@memaro/components/ButtonOverlay.tsx";
import LoadingOverlay from "@memaro/components/LoadingOverlay.tsx";
import {useFeedback} from "@memaro/feedback";
import {styled} from "@mui/material/styles";
import Box from "@mui/material/Box";
import {doNotForwardProps} from "../lib/styled.ts";
import FileUploadIcon from "@mui/icons-material/FileUpload";

export type AudioSectionProps = Omit<SectionProps, 'padding'> & {
    profileID: string
    src?: string
    onChange: (sectionID: string, src: string) => void
}

type ContainerProps = {
    showButtonOverlay: boolean
}

const Container = styled(Box, doNotForwardProps('showButtonOverlay'))<ContainerProps>(({showButtonOverlay}) => ({
    position: "relative",
    width: "100%",
    minHeight: 125,
    overflow: 'hidden',

    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',

    'audio': {
        transition: 'margin-bottom 0.3s ease',
        marginBottom: showButtonOverlay ? 66 : 0,
    },

    '&.editmode:hover audio': {
        marginBottom: 66,
    },

    ':hover .overlay': {
        opacity: 0.9,
    }
}))

const AudioSection: FC<AudioSectionProps> = ({profileID, sectionID, src, onChange, editMode, ...sectionProps}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const {error} = useFeedback()

    const handleFileSelected = useCallback((file: File) => {
        setLoading(true)
        apiClient.upload(profileID, "audio", file)
            .then((m) => onChange(sectionID, m))
            .catch(() => error?.("Er is een fout opgetreden bij het uploaden van het audio-bestand"))
            .finally(() => setLoading(false))
    }, [profileID, onChange, sectionID, error])

    return (
        <Section {...sectionProps} editMode={editMode} sectionID={sectionID} padding={0}>
            <Container showButtonOverlay={!src || loading} className={editMode ? 'editmode' : ''}>
                <AudioPlayer src={src || undefined} />
                {editMode ? (
                    <ButtonOverlay opacity={!src || loading ? 0.9 : 0}>
                        <UploadButton onFileSelected={handleFileSelected} disabled={loading} accept="audio/*">
                            <FileUploadIcon />
                            <span>Audio uploaden</span>
                        </UploadButton>
                    </ButtonOverlay>
                ) : null}
                <LoadingOverlay opacity={loading ? 1 : 0} />
            </Container>
        </Section>
    )
}

export default AudioSection
